import { Box, Menu, MenuItem, Typography } from "@mui/material";
import React from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import TicketsIcon from "../../../../assets/icons/ticketsIcon";
import ResolvedTicketIcon from "../../../../assets/icons/resolvedTicketIcon";
import ShareIcon from "../../../../assets/icons/shareIcon";
import { colorMapping } from "@config/statusColorMapping.ts";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/en';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.locale('en');

interface TicketDataProps {
    ticketsData?: {
        id: number;
        subject: string;
        description: string;
        status: string;
        createdAt: string;
        updatedAt: string;
        customStatusId: number;
        organizationId: number;
        tags: string[];
    }[];
    customStatuses?: {
        active: boolean;
        id: number;
        statusCategory: string;
        agentLabel: string;
        endUserLabel: string;
    }[];
    organizations?: {
        id: number;
        name: string;
    }[];
    handleClick: (e: any, label: string, data: any) => void;
    handleClose: () => void;
    handleTicketAction: (action: string) => void;
    open: boolean;
    anchorEl: null | HTMLElement;
    activeTickets: string;
    handleTicketNavigation: (ticketId: number) => void;
    hideResolved: boolean;
}

const TicketsCard: React.FC<TicketDataProps> = (props) => {
    const {
        ticketsData,
        customStatuses,
        organizations,
        handleClick,
        handleClose,
        handleTicketAction,
        open,
        anchorEl,
        activeTickets,
        handleTicketNavigation,
        hideResolved
    } = props;

    const convertUTCDateToLocal = (utcDateString: string, formatType: "date" | "time" | "both" = "both"): string => {
        try {
            const utcDate = dayjs.utc(utcDateString).local();
            if (!utcDate.isValid()) {
                throw new Error("Invalid date");
            }
            if (formatType === "date") {
                return utcDate.format('DD-MMM-YYYY');
            } else if (formatType === "time") {
                return utcDate.format('HH:mm');
            } else {
                return utcDate.format('DD-MMM-YYYY HH:mm');
            }
        } catch (error) {
            console.error("Error in converting date:", error);
            return "Invalid date";
        }
    }
    return (
        <>
            {
                ticketsData?.map((data) => (
                    <Box
                        key={data?.id}
                        className={`flex flex-row justify-between px-4 py-3  ${activeTickets === "closed" && "opacity-[41%]"
                            }`}
                    >
                        <Box
                            className="flex flex-row gap-4 cursor-pointer"
                            onClick={() => handleTicketNavigation(data?.id)}
                        >
                            {customStatuses &&
                                (() => {
                                    const matchStatus = customStatuses?.find(
                                        (status) => status?.id == data?.customStatusId
                                    );
                                    if (matchStatus) {
                                        return (
                                            <Typography key={matchStatus.endUserLabel} className="flex items-center">
                                                <TicketsIcon
                                                    style={{
                                                        backgroundColor: colorMapping[matchStatus.endUserLabel],
                                                    }}
                                                    fill={"#fff"}
                                                />
                                            </Typography>
                                        );
                                    }
                                    return null;
                                })()}
                            <Box className="flex flex-col justify-center ">
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        "@media(min-width:374px)": {
                                            minWidth: "9rem",
                                        },
                                        "@media(min-width:768px)": {
                                            minWidth: "9.5rem",
                                        },
                                    }}
                                    className="text-sm"
                                >
                                    {data?.subject?.length > 15
                                        ? data?.subject.slice(0, 14) + "..."
                                        : data?.subject}
                                </Typography>
                                <Typography variant="subtitle2">TicketId: {data.id}</Typography>
                            </Box>
                            <Box className="flex flex-col justify-center">
                                <Typography variant="subtitle2">
                                    {convertUTCDateToLocal(data?.updatedAt, "date")} | {convertUTCDateToLocal(data?.updatedAt, "time")}
                                </Typography>

                                {organizations &&
                                    (() => {
                                        const matchOrganization = organizations.find(
                                            (organization) =>
                                                organization?.id === data?.organizationId
                                        );
                                        if (matchOrganization) {
                                            return (
                                                <Typography
                                                    key={matchOrganization?.name}
                                                    variant="subtitle2"
                                                    className="hidden md:block"
                                                >
                                                    {matchOrganization?.name}
                                                </Typography>
                                            );
                                        }
                                        return null;
                                    })()}
                            </Box>
                        </Box>
                        {activeTickets !== "closed" && (
                            <Box component="div" className="flex items-center ">
                                <BsThreeDotsVertical
                                    size={20}
                                    onClick={(e) => handleClick(e, "resolved", data)}
                                />
                            </Box>
                        )}
                    </Box>
                ))
            }
            <Menu
                id="menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                autoFocus={false}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                sx={{
                    "& .MuiPaper-root": {
                        boxShadow: "none",

                        border: "1px solid #ccc",
                        marginTop: "1.2rem",
                    },
                    "& .MuiMenu-list": {
                        padding: 0,
                    },
                }}
            >
                <MenuItem
                    sx={{ borderBottom: "1px solid" }}
                    className="!border-b-1 !border-zinc-200"
                    onClick={() => handleTicketAction("resolved")}
                    disabled={hideResolved}
                >
                    <ResolvedTicketIcon />
                    <Typography className="pl-2 pr-6 text-zinc-500">Resolve</Typography>
                </MenuItem>
                <MenuItem onClick={() => handleTicketAction("share")}>
                    <ShareIcon />
                    <Typography className="pl-2 pr-6 text-zinc-500">Share</Typography>
                </MenuItem>
            </Menu>
        </>
    );
};

export default TicketsCard;
